/**
 * Mobile Page Lifecycle Hook
 *
 * PROBLEM: Mobile browsers handle backgrounded pages differently than desktop:
 * - They freeze/discard pages to save memory
 * - When restored, the page may fully re-initialize (showing loading spinner)
 * - This only happens on REAL mobile devices, not Chrome DevTools emulation
 *
 * SOLUTION: This hook handles 4 mobile-specific browser behaviors:
 *
 * 1. bfcache (Back-Forward Cache) - pageshow event with persisted flag
 * 2. Page Lifecycle API - freeze/resume events (Chrome mobile)
 * 3. Visibility change - document visibility state
 * 4. State persistence - survive page discards with sessionStorage
 *
 * @created 2025-12-31 for GAP-MOBILE-002
 */

import { useEffect, useRef, useCallback } from 'react';

// Debug mode - set to true to see console logs
const DEBUG = true;

const log = (...args) => {
  if (DEBUG) {
    console.log('[MobileLifecycle]', new Date().toISOString().slice(11, 23), ...args);
  }
};

/**
 * Persists critical auth state to sessionStorage
 * This survives minor page discards but not full browser closes
 */
const STATE_KEY = 'scrolled_mobile_state';

export const persistState = (state) => {
  try {
    const toSave = {
      ...state,
      timestamp: Date.now(),
    };
    sessionStorage.setItem(STATE_KEY, JSON.stringify(toSave));
    log('State persisted:', Object.keys(state));
  } catch (e) {
    log('Failed to persist state:', e);
  }
};

export const getPersistedState = () => {
  try {
    const saved = sessionStorage.getItem(STATE_KEY);
    if (!saved) return null;

    const state = JSON.parse(saved);
    const age = Date.now() - state.timestamp;

    // State is valid for 30 minutes
    if (age > 30 * 60 * 1000) {
      log('Persisted state expired (age:', Math.round(age / 1000 / 60), 'min)');
      sessionStorage.removeItem(STATE_KEY);
      return null;
    }

    log('Retrieved persisted state (age:', Math.round(age / 1000), 'sec)');
    return state;
  } catch (e) {
    log('Failed to get persisted state:', e);
    return null;
  }
};

export const clearPersistedState = () => {
  sessionStorage.removeItem(STATE_KEY);
  log('Persisted state cleared');
};

/**
 * Main hook for handling mobile page lifecycle
 *
 * @param {Object} options
 * @param {Function} options.onRestore - Called when page is restored (should skip re-init)
 * @param {Function} options.onFreeze - Called when page is about to freeze
 * @param {Function} options.onResume - Called when page resumes from freeze
 * @param {Function} options.getState - Returns current state to persist
 */
export const useMobilePageLifecycle = ({
  onRestore,
  onFreeze,
  onResume,
  getState,
} = {}) => {
  const isRestoredRef = useRef(false);
  const wasFrozenRef = useRef(false);

  // Track if this is a bfcache restoration
  useEffect(() => {
    const handlePageShow = (event) => {
      log('pageshow event - persisted:', event.persisted);

      if (event.persisted) {
        // Page was restored from bfcache
        log('Page restored from bfcache - skipping re-initialization');
        isRestoredRef.current = true;
        onRestore?.();
      }
    };

    const handlePageHide = (event) => {
      log('pagehide event - persisted:', event.persisted);

      if (event.persisted && getState) {
        // Page is going to bfcache - persist state
        const state = getState();
        if (state) {
          persistState(state);
        }
      }
    };

    window.addEventListener('pageshow', handlePageShow);
    window.addEventListener('pagehide', handlePageHide);

    return () => {
      window.removeEventListener('pageshow', handlePageShow);
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, [onRestore, getState]);

  // Handle Page Lifecycle API (Chrome mobile)
  useEffect(() => {
    const handleFreeze = () => {
      log('Page frozen (Page Lifecycle API)');
      wasFrozenRef.current = true;

      if (getState) {
        const state = getState();
        if (state) {
          persistState(state);
        }
      }

      onFreeze?.();
    };

    const handleResume = () => {
      log('Page resumed (Page Lifecycle API)');

      if (wasFrozenRef.current) {
        wasFrozenRef.current = false;
        isRestoredRef.current = true;
        onResume?.();
      }
    };

    // Page Lifecycle API events (Chrome 68+)
    document.addEventListener('freeze', handleFreeze);
    document.addEventListener('resume', handleResume);

    return () => {
      document.removeEventListener('freeze', handleFreeze);
      document.removeEventListener('resume', handleResume);
    };
  }, [onFreeze, onResume, getState]);

  // Handle visibility change (fallback for browsers without Page Lifecycle API)
  useEffect(() => {
    let hiddenTimestamp = null;

    const handleVisibilityChange = () => {
      if (document.hidden) {
        log('Page became hidden');
        hiddenTimestamp = Date.now();

        // Persist state when hiding
        if (getState) {
          const state = getState();
          if (state) {
            persistState(state);
          }
        }
      } else {
        const hiddenDuration = hiddenTimestamp ? Date.now() - hiddenTimestamp : 0;
        log('Page became visible (was hidden for', Math.round(hiddenDuration / 1000), 'sec)');

        // If hidden for more than 5 seconds, check for restored state
        if (hiddenDuration > 5000) {
          const savedState = getPersistedState();
          if (savedState) {
            log('Found saved state after long hide - treating as restore');
            isRestoredRef.current = true;
            onRestore?.();
          }
        }

        hiddenTimestamp = null;
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [onRestore, getState]);

  // Check for persisted state on mount (handles full page reload after discard)
  useEffect(() => {
    const savedState = getPersistedState();
    if (savedState) {
      log('Found persisted state on mount - page was likely discarded and reloaded');
      isRestoredRef.current = true;
      onRestore?.();
    }
  }, []); // Only on mount

  return {
    isRestored: isRestoredRef.current,
    wasFrozen: wasFrozenRef.current,
  };
};

/**
 * Simplified hook that just tells you if you should skip initialization
 * Use this in AuthContext to prevent re-fetching on mobile restore
 */
export const useShouldSkipInit = () => {
  const shouldSkipRef = useRef(false);

  useEffect(() => {
    // Check for persisted state on mount
    const savedState = getPersistedState();
    if (savedState && savedState.isAuthenticated) {
      log('Should skip init - found valid persisted auth state');
      shouldSkipRef.current = true;
    }

    // Listen for restore events
    const handlePageShow = (event) => {
      if (event.persisted) {
        log('Should skip init - bfcache restore');
        shouldSkipRef.current = true;
      }
    };

    const handleResume = () => {
      log('Should skip init - page resumed');
      shouldSkipRef.current = true;
    };

    window.addEventListener('pageshow', handlePageShow);
    document.addEventListener('resume', handleResume);

    return () => {
      window.removeEventListener('pageshow', handlePageShow);
      document.removeEventListener('resume', handleResume);
    };
  }, []);

  return shouldSkipRef.current;
};

/**
 * Global initialization guard
 * Prevents multiple initializations on mobile restore
 */
let initializationCount = 0;
let lastInitTime = 0;

export const shouldInitialize = (componentName = 'Unknown') => {
  const now = Date.now();
  const timeSinceLastInit = now - lastInitTime;

  // If initialized within last 5 seconds, skip
  if (timeSinceLastInit < 5000 && initializationCount > 0) {
    log(`[${componentName}] Skipping init - last init was ${timeSinceLastInit}ms ago`);
    return false;
  }

  // Check for persisted state
  const savedState = getPersistedState();
  if (savedState && savedState.isAuthenticated) {
    log(`[${componentName}] Skipping init - found valid persisted state`);
    return false;
  }

  initializationCount++;
  lastInitTime = now;
  log(`[${componentName}] Proceeding with init #${initializationCount}`);
  return true;
};

export const resetInitializationGuard = () => {
  initializationCount = 0;
  lastInitTime = 0;
  log('Initialization guard reset');
};

export default useMobilePageLifecycle;
